// proposal-scope-guard.ts
//
// Luật cho `allowed_paths` mà một proposal tự khai. Tách khỏi
// proposal-applier.ts (257 dòng, quá ngân sách 200) — applier giữ phần giao
// dịch, file này giữ phần "worker được khai phạm vi tới đâu".

import { coversAControllerOwnedPath, isControllerOwnedPath } from "./controller-owned-paths.js";

// P22: `allowed_paths` is the boundary every later guard measures this
// ticket's work against — the verifier's scope check reads it, and the
// Edit-family adapters (implement/testcase/test) are bounded by NOTHING
// else. Letting a proposal author that field is the governed party writing
// the rule it will be judged by: the Bug K/Bug L shape, and measurably the
// first link of a real chain — a ticket scoped `**` gets Edit-family, and
// `.avatar/verification-profile.json` (the recipe list `checkRecipeIsApproved`
// honours, re-read live each tick) was inside the worktree and matched by
// that same `**`.
//
// Three rules, all narrow on purpose. An unbounded scope is refused outright;
// so is a git-magic pathspec (leading `:`, review 260930 M1) — the verifier
// reads it as literal text matching no file while `git add` reads `:!x` as
// "everything but x" and `:/` as the repo root, so the two readers disagree
// about how wide it is; a scope reaching a controller-owned path is refused by
// the SAME predicate the verifier uses (controller-owned-paths.ts), so the two
// can never drift into disagreeing about what a worker may claim. Everything
// else — ordinary
// `src/**`, `test/**`, explicit file lists — passes untouched, because a
// plan worker proposing normally-scoped tickets is the mission's main path
// and a guard that blocks it is a guard someone will delete.
const UNBOUNDED_SCOPE_PATTERNS = new Set(["**", "*", "/", "./", ".", "**/*"]);

/** Rejection reason for a proposal-authored `allowed_paths`, or undefined when the scope is acceptable. */
export function checkProposedScope(allowedPaths: string[] | undefined): string | undefined {
  if (allowedPaths === undefined) return undefined;
  for (const pattern of allowedPaths) {
    const trimmed = pattern.trim();
    if (UNBOUNDED_SCOPE_PATTERNS.has(trimmed)) {
      return (
        `proposal khai allowed_paths "${pattern}" — phạm vi không giới hạn. ` +
        'Ticket do worker đề xuất phải khai phạm vi cụ thể (vd "src/**"); ' +
        "mở toàn repo là tự cấp quyền cho chính mình, cần người duyệt."
      );
    }
    if (trimmed.startsWith(":")) {
      return (
        `proposal khai allowed_paths "${pattern}" — pathspec đặc biệt của git (bắt đầu bằng ":"). ` +
        'Verifier đọc nó như chữ thường không khớp file nào, git lại hiểu ":!x" là "mọi thứ trừ x" ' +
        'và ":/" là gốc repo ⇒ phạm vi không đo được. Khai đường dẫn/glob cụ thể (vd "src/**").'
      );
    }
    // A pattern is refused when it can reach a controller-owned path — both
    // the literal path and the glob that would match it.
    if (isControllerOwnedPath(trimmed) || coversAControllerOwnedPath(trimmed)) {
      return (
        `proposal khai allowed_paths "${pattern}" chạm đường của controller ` +
        "(binding/verification-profile) — worker không được sửa thứ nó bị kiểm bằng."
      );
    }
  }
  return undefined;
}
